import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAudioContext } from "../../../domains/app/contexts/audio.context";
import { memoryStore, useStore } from "../../../lib/store";
import { RAIL_SECTION_ATTRIBUTE } from "../../../pages/article/desktop/useSectionRail";
import {
  sectionAt,
  speechSections,
  type SpeechSectionId,
} from "../lib/speechSections";
import type { ListenableArticle } from "./useDesktopArticleListening";

/** Where the section being read sits against the reader's viewport. */
export type NowReadingPosition = "above" | "visible" | "below";

/**
 * The passage the narration reads, as the open article page lays it out. The
 * page publishes it while it shows the article being played; the sidebar
 * player reads it for its "Now reading" link, which it hides elsewhere.
 */
type NowReadingSurface = {
  articleId: string;
  sectionId: SpeechSectionId;
  title: string;
  position: NowReadingPosition;
  reveal: () => void;
};

const nowReadingStore = memoryStore<NowReadingSurface | null>(null);

export function useNowReadingSurface(): NowReadingSurface | null {
  const [surface] = useStore(nowReadingStore);
  return surface;
}

function sectionElement(
  container: HTMLElement,
  id: SpeechSectionId
): HTMLElement | null {
  return container.querySelector<HTMLElement>(
    `[${RAIL_SECTION_ATTRIBUTE}="${id}"]`
  );
}

function positionIn(
  container: HTMLElement,
  element: HTMLElement
): NowReadingPosition {
  const view = container.getBoundingClientRect();
  const box = element.getBoundingClientRect();
  if (box.bottom <= view.top) return "above";
  if (box.top >= view.bottom) return "below";
  return "visible";
}

/**
 * Publishes the section of `article` the narration is reading, when it is the
 * loaded track, estimated from the playback position (see `speechSections`):
 * the page marks it, the sidebar player links to it. It follows every
 * playback tick, so the page mounts it in a leaf, not in its own body.
 */
export function useNowReading(
  article: ListenableArticle,
  scrollRef: React.RefObject<HTMLElement | null>
): void {
  const { t } = useTranslation();
  const { currentMediaContent, hasTrack, currentTime, duration } =
    useAudioContext();
  const sections = useMemo(() => speechSections(article), [article]);
  const isLoaded =
    hasTrack &&
    currentMediaContent !== null &&
    currentMediaContent.type === "article_audio" &&
    currentMediaContent.source.id === article.id;
  // A live stream has no length yet: nothing says how far into it we are.
  const sectionId =
    isLoaded && duration > 0
      ? sectionAt(sections, Math.min(currentTime / duration, 1))
      : null;
  const [position, setPosition] = useState<NowReadingPosition | null>(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (container === null || sectionId === null) {
      setPosition(null);
      return;
    }
    function update(): void {
      if (container === null || sectionId === null) return;
      const element = sectionElement(container, sectionId);
      // An article shown through its abstract has no digest sections.
      setPosition(element === null ? null : positionIn(container, element));
    }
    update();
    container.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(container);
    return () => {
      container.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [scrollRef, sectionId]);

  const titles: Record<SpeechSectionId, string> = {
    background: t("Background"),
    objectives: t("Objective"),
    methods: t("Methods"),
    results: t("Results"),
    limitations: t("Limitations"),
    conclusion: t("Conclusion"),
  };
  const title = sectionId === null ? null : titles[sectionId];
  const articleId = article.id;

  useEffect(() => {
    if (sectionId === null || title === null || position === null) {
      nowReadingStore.set(null);
      return;
    }
    const surface: NowReadingSurface = {
      articleId,
      sectionId,
      title,
      position,
      reveal: () => {
        // The page unpublishes on unmount, and `position` exists only for a
        // section it measured, so both are there while the link is offered.
        const container = scrollRef.current;
        if (container === null) {
          throw new Error("should not happen: no article scroll container");
        }
        const element = sectionElement(container, sectionId);
        if (element === null) {
          throw new Error(`should not happen: no section "${sectionId}"`);
        }
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      },
    };
    nowReadingStore.set(surface);
    return () => {
      // Only the page's own surface: a newer page may have published since.
      if (nowReadingStore.get() === surface) nowReadingStore.set(null);
    };
  }, [articleId, sectionId, title, position, scrollRef]);
}
